/**
 * PLP featured product: `?prd=<handle>` pins that product above the grid.
 * Liquid can't read query params, so the card is fetched client-side.
 *
 * <plp-featured-product>  placeholder that fetches + injects the card and hides
 *                         the product's regular grid card.
 * <product-card-featured> the card: size chips, size gate, size chart trigger.
 */

const PARAM = 'prd';
const SECTION_ID = 'plp-featured-product';

/** Load a classic script once (no-op if already on the page). */
function loadScript(src) {
  if (!src || document.querySelector(`script[src="${src}"]`)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.append(script);
  });
}

class PlpFeaturedProduct extends HTMLElement {
  connectedCallback() {
    const handle = new URLSearchParams(window.location.search).get(PARAM)?.trim();
    if (!handle) {
      this.remove();
      return;
    }
    this.load(handle);
  }

  async load(handle) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = html.querySelector('product-card-featured');
      if (!card) throw new Error('No card in response');

      if (!customElements.get('product-form')) await loadScript(this.dataset.productFormSrc);
      if (card.querySelector('size-chart') && !customElements.get('size-chart')) {
        await loadScript(this.dataset.productPageSrc);
      }

      // A <style> rather than removing the <li>, so infinite-scroll pages and facet re-renders are covered too.
      const hideDuplicate = document.createElement('style');
      hideDuplicate.textContent = `.plp__product[data-product-handle="${CSS.escape(handle)}"] { display: none; }`;

      this.replaceChildren(hideDuplicate, card);
      this.removeAttribute('data-loading');
    } catch (error) {
      console.warn(`[plp-featured-product] Could not load "${handle}":`, error);
      this.remove();
    }
  }
}

class ProductCardFeatured extends HTMLElement {
  connectedCallback() {
    this.idInput = this.querySelector('form [name="id"]');
    this.sizeError = this.querySelector('[data-size-error]');
    this.hasSizes = !!this.querySelector('input[data-variant-id]');

    this.addEventListener('change', this.onChange);
    // Capture on the ancestor so the gate runs before product-form.js's submit handler.
    this.addEventListener('submit', this.onSubmit, true);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit, true);
    this.removeEventListener('click', this.onClick);
  }

  onChange = (event) => {
    const input = event.target.closest('input[data-variant-id]');
    if (!input) return;
    this.idInput.value = input.dataset.variantId;
    if (this.sizeError) this.sizeError.hidden = true;
  };

  onSubmit = (event) => {
    if (!this.hasSizes || this.querySelector('input[data-variant-id]:checked')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.sizeError) this.sizeError.hidden = false;
  };

  onClick = (event) => {
    if (!event.target.closest('[data-featured-size-chart]')) return;
    event.preventDefault();
    this.querySelector('size-chart')?.open();
  };

  /* Called by <size-chart> when a size is chosen from the chart. */
  selectSize(value) {
    const input = this.querySelector(`input[data-variant-id][value="${CSS.escape(value)}"]:not(:disabled)`);
    if (input && !input.checked) input.click();
  }
}

if (!customElements.get('plp-featured-product')) {
  customElements.define('plp-featured-product', PlpFeaturedProduct);
}
if (!customElements.get('product-card-featured')) {
  customElements.define('product-card-featured', ProductCardFeatured);
}
